import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetProductionListQuery = ApiSearchQuery<
  '/v2/pn-force/productions',
  'get'
>;
export type GetProductionListResponse = ApiResponseBody<
  '/v2/pn-force/productions',
  'get'
>;
export type ProductionListItem = GetProductionListResponse['items'][0];

export type ProductionOrderBy = NonNullable<GetProductionListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetProductionCountQuery = ApiSearchQuery<
  '/v2/pn-force/productions/count',
  'get'
>;
export type GetProductionCountResponse = ApiResponseBody<
  '/v2/pn-force/productions/count',
  'get'
>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetProductionResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}',
  'get'
>;

// ---------------------------------------------------------------------------
// Overview API Types(プロダクション詳細「概要」タブ用集計)
// ---------------------------------------------------------------------------

export type GetProductionOverviewResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/overview',
  'get'
>;

// ---------------------------------------------------------------------------
// Values API Types(プロダクション詳細「価値」タブ用一覧)
// ---------------------------------------------------------------------------

export type GetProductionValuesResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/values',
  'get'
>;
export type ProductionValueItem = GetProductionValuesResponse['items'][number];

// ---------------------------------------------------------------------------
// Value Scope (Production 詳細 > 価値スコープ編集) API Types
// ---------------------------------------------------------------------------

/** 価値スコープ追加候補 Value 一覧の query（productionId は path 由来で固定注入）。 */
export type GetAssignableValuesQuery = ApiSearchQuery<
  '/v2/pn-force/productions/{id}/assignable-values',
  'get'
>;
export type GetAssignableValuesResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/assignable-values',
  'get'
>;
export type AssignableValueItem = GetAssignableValuesResponse['items'][number];

/** 価値スコープへ Value を一括追加する body。 */
export type PostScopeValuesRequest = ApiRequestBody<
  '/v2/pn-force/productions/{id}/scope-values',
  'post'
>;
export type PostScopeValuesResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/scope-values',
  'post'
>;

export type DeleteScopeValueResponse = ApiResponseBody<
  '/v2/pn-force/productions/{id}/scope-values/{valueId}',
  'delete'
>;

// ---------------------------------------------------------------------------
// Production Enums (BE 由来 4 / 5 値)
// ---------------------------------------------------------------------------

export const ProductionStatus = {
  PLANNED: 'PLANNED',
  IN_PROGRESS: 'IN_PROGRESS',
  APPROVED: 'APPROVED',
  RELEASED: 'RELEASED',
} as const;
export type ProductionStatus =
  (typeof ProductionStatus)[keyof typeof ProductionStatus];

const PRODUCTION_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(ProductionStatus),
);
export function isProductionStatus(
  value: string | null | undefined,
): value is ProductionStatus {
  return value != null && PRODUCTION_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type ProductionStatusFilter = ProductionStatus | 'ALL';

export const ReleaseLevel = {
  MAJOR: 'MAJOR',
  MINOR: 'MINOR',
  PATCH: 'PATCH',
  REVISION: 'REVISION',
  HOTFIX: 'HOTFIX',
} as const;
export type ReleaseLevel = (typeof ReleaseLevel)[keyof typeof ReleaseLevel];

const RELEASE_LEVEL_VALUES: ReadonlySet<string> = new Set(
  Object.values(ReleaseLevel),
);
export function isReleaseLevel(
  value: string | null | undefined,
): value is ReleaseLevel {
  return value != null && RELEASE_LEVEL_VALUES.has(value);
}
